import React, { useCallback } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useNavigate } from 'react-router-dom'

import { XStack, Stack, Select, getTokenValue, useTheme } from 'tamagui'

import {
  Button,
  Paragraph,
  Icon,
  useCreateLinkPressHandler,
} from '@newComponents'

import { useMutation } from '@data/useMutation'

import {
  StyledSelect,
  StyledSelectContent,
  StyledSelectItem,
  StyledSelectItemText,
  StyledSelectViewport,
} from '@newComponents/Form/FormSelect'

import {
  EventScreenSettingsButton_event$key,
  EventVisibility,
} from './__generated__/EventScreenSettingsButton_event.graphql'
import { EventScreenSettingsButtonUpdateMutation } from './__generated__/EventScreenSettingsButtonUpdateMutation.graphql'

const VISIBILITY_TEXT: Record<EventVisibility, string> = {
  DRAFT: 'DRAFT',
  PUBLIC: 'Public',
  PRIVATE: 'Private',
  '%future added value': 'Unknown',
}

export const EventScreenSettingsButton = ({
  event,
}: {
  event: EventScreenSettingsButton_event$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenSettingsButton_event on Event {
        id

        prettyUrl
        visibility

        hasVenue
        hasExternalUrl

        description

        owner {
          ... on Guild {
            myMembership {
              membershipLevel
            }
          }

          ... on User {
            isMe
          }
        }
      }
    `,
    event
  )

  const updateEvent =
    useMutation<EventScreenSettingsButtonUpdateMutation>(graphql`
      mutation EventScreenSettingsButtonUpdateMutation(
        $input: UpdateEventByIdInput!
      ) {
        updateEventById(input: $input) {
          event {
            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
          }
        }
      }
    `)

  const theme = useTheme()

  const navigate = useNavigate()
  const linkAction = useCallback(
    () => navigate(`/events/${data.prettyUrl}/settings`),
    [data.prettyUrl]
  )

  const { linkPressHandler, anchorRef } = useCreateLinkPressHandler(linkAction)

  if (
    data.owner?.isMe ||
    ['ORGANIZER', 'EDITOR'].includes(
      data.owner?.myMembership?.membershipLevel || ''
    )
  ) {
    return (
      <XStack
        gap='$6'
        alignItems='center'
        $gtXs={{ paddingLeft: '$5' }}
        $gtSm={{
          paddingLeft: 0,
        }}
      >
        {data.visibility === 'DRAFT' &&
        data.description &&
        (data.hasVenue || data.hasExternalUrl) ? (
          <StyledSelect
            onValueChange={async (value: EventVisibility) => {
              await updateEvent({
                variables: {
                  input: { id: data.id, patch: { visibility: value } },
                },
              })
            }}
          >
            <Select.Trigger
              flexBasis='content'
              padding={0}
              borderWidth={0}
              outlineWidth={0}
            >
              <Button
                borderRadius={14}
                gap='$1'
                height={34}
                style={{ background: theme.errorColor.val }}
                hoverStyle={{
                  background: theme.errorColorHover.val,
                }}
              >
                <Button.Text size='$4'>DRAFT</Button.Text>
              </Button>
            </Select.Trigger>

            <StyledSelectContent zIndex={200_000}>
              <StyledSelectViewport>
                <StyledSelectItem index={0} value='PUBLIC'>
                  <XStack gap='$2' padding='$1'>
                    <StyledSelectItemText>Make Public</StyledSelectItemText>
                  </XStack>
                </StyledSelectItem>
              </StyledSelectViewport>
            </StyledSelectContent>
          </StyledSelect>
        ) : (
          <Paragraph
            color={data.visibility === 'DRAFT' ? '$errorColor' : '$grey7'}
            fontWeight='$6'
          >
            {VISIBILITY_TEXT[data.visibility]}
          </Paragraph>
        )}

        <XStack flex={1} justifyContent='flex-end'>
          <Button
            ref={anchorRef}
            tag='a'
            href={`/events/${data.prettyUrl}/settings`}
            secondary
            borderRadius={14}
            gap='$1'
            paddingLeft='$3'
            height={34}
            onPress={linkPressHandler}
            successFlash={false}
            errorFlash={false}
          >
            <Icon name='settings-filled' color='$grey8' size='$4' />

            <Button.Text secondary size='$4'>
              Settings
            </Button.Text>
          </Button>
        </XStack>
      </XStack>
    )
  }

  return null
}
